/**
 * +field: hero ability unlocks (ClassDef.unlocks, docs/design/zones.md "Unlocks"). A level-1 hero
 * has Dodge plus a 2–3 button builder/spender core that kills field mobs solo; the rest of the kit
 * arrives one button per level or so, and everything is open by level 10 (the Phase 1 cap).
 * Transform forms (Aegis Recall, Sunfall, Ravager's Finale, Tempest) unlock with the button that
 * grants them, so a pressed key never resolves to a locked override.
 */
import type { AbilityId } from '@keystone/sim';
import { ABILITY } from '../ids';

type Unlocks = Partial<Record<AbilityId, number>>;

const W = ABILITY.warden;
const L = ABILITY.lightbinder;
const B = ABILITY.blademaster;
const S = ABILITY.stormcaller;
const DODGE = ABILITY.common.dodge;

/** Warden: Shield Slam / Cleave Strike build Resolve, Bulwark Strike spends it for self-healing. */
export const WARDEN_UNLOCKS: Unlocks = {
  [DODGE]: 1,
  [W.shieldSlam]: 1,
  [W.cleaveStrike]: 1,
  [W.bulwarkStrike]: 1,
  [W.taunt]: 2,
  [W.consecratedGround]: 3,
  [W.shieldBash]: 4,
  [W.aegisThrow]: 5,
  [W.aegisRecall]: 5,
  [W.riposte]: 6,
  [W.shieldWallBash]: 7,
  [W.grip]: 8,
  [W.lastStand]: 10,
};

/** Lightbinder: Judgment (filler) + Holy Shock (damage or heal) build Holy Power; Flash Heal keeps you up. */
export const LIGHTBINDER_UNLOCKS: Unlocks = {
  [DODGE]: 1,
  [L.judgment]: 1,
  [L.holyShock]: 1,
  [L.flashHeal]: 1,
  [L.wordOfLight]: 2,
  [L.cleanse]: 3,
  [L.radiantWave]: 4,
  [L.dawnsong]: 5,
  [L.guardiansAegis]: 6,
  [L.divineShield]: 7,
  [L.blessingOfFreedom]: 8,
  [L.dawnbringer]: 10,
  [L.sunfall]: 10,
};

/** Blademaster: Overpower builds Wounds and Rage, Mortal Strike cashes them, Slam fills. */
export const BLADEMASTER_UNLOCKS: Unlocks = {
  [DODGE]: 1,
  [B.overpower]: 1,
  [B.mortalStrike]: 1,
  [B.slam]: 1,
  [B.cleave]: 2,
  [B.charge]: 3,
  [B.execute]: 4,
  [B.dieByTheSword]: 5, // grind harness: a defensive before Bramblewood's bandits
  [B.rend]: 6,
  [B.colossusSmash]: 7,
  [B.pummel]: 8,
  [B.bladestorm]: 10,
  [B.ravagersFinale]: 10,
};

/** Stormcaller: Lightning Bolt builds Maelstrom, Earth Shock spends it (every 3rd → Tempest), Flame Shock dots. */
export const STORMCALLER_UNLOCKS: Unlocks = {
  [DODGE]: 1,
  [S.lightningBolt]: 1,
  [S.earthShock]: 1,
  [S.flameShock]: 1,
  [S.tempest]: 1,
  [S.chainLightning]: 2,
  [S.lavaBurst]: 3,
  [S.windShear]: 4,
  [S.earthquake]: 5,
  [S.astralShift]: 6,
  [S.stormkeeper]: 8,
  [S.arcTorrent]: 10,
};
